import * as THREE from 'three';
import EventDispatcher from '../common/EventDispatcher';
import { gsap, Expo, Cubic, Power0 } from 'gsap';
import { Global } from '../common/global';
import { isMobile } from 'mobile-device-detect';

// --- Camera Behavior Constants (Copied from the previous demo logic) ---
const CAMERA_IDLE_DISTANCE = 3;
const CAMERA_MAX_DISTANCE = 3;
const CAMERA_HEIGHT = 2;
const POSITION_LERP_FACTOR = 0.05 * 0.1; // How smoothly the camera follows the position
const MOVE_LERP_FACTOR = 0.03; // How quickly the distance ramps up/down
const LOOK_AT_LERP_FACTOR = 0.1; // How smoothly the camera looks at the target

const INITIAL_CAMERA_POSITION = new THREE.Vector3(36.5, 16, -10);
const INITIAL_LOOK_AT_POSITION = new THREE.Vector3(36.5, 16, -50);

// Existing constants for smoothing and padding
const CAMERA_FOLLOW_LERP_X = 0.5;
const CAMERA_FOLLOW_LERP_Y = 0.1;
const CAMERA_FOLLOW_LERP_Z = 0.5;
const CAMERA_LOOKAT_Y_PADD = 1.5;
const DEFAULT_SHAKE_VALUE = {
  value: 0.0,
};

export default class Camera extends THREE.PerspectiveCamera {
  skyLookFactor = new THREE.Vector3(0, 2, -5);
  lerpZReduce = 0;
  lastCameraPosition = INITIAL_CAMERA_POSITION.clone();
  gameStarted = false;

  // State variables to store the player's latest kinematic data
  _playerPos = new THREE.Vector3();
  _playerRotY = 0;
  _playerIsMoving = false; // New property to track movement state

  _currentMoveSpeed = 0; // Speed factor (0 to 1) for dynamic distance

  constructor(fov, ratio, near, far) {
    super(fov, ratio, near, far);

    this.onEvents = this.onEvents.bind(this);
    // Explicitly bind the handler for the new player data event
    this.onPlayerUpdate = this.onPlayerUpdate.bind(this);
  }

  init(renderer) {
    this.idleGap = { value: 0 };
    this.inActive = true;
    this.isIdle = false;
    this.isIdleInfo = { value: 0 };
    this.renderer = renderer;
    this.introbj = { value: 0 };
    this.introbj2 = { value: 0 };
    this.initPanCompleted = false;
    this.panY = {
      value: 0,
    };
    this.camerastart = null;
    this.canShakeCamera = false;
    this._lookTarget = null;
    this._lookSmoothing = isMobile ? 0.4 : 0.05;
    this.lookAtSpeed = {
      value: 0,
    };
    this.zoomFact = {
      value: 0,
    };
    this.events = EventDispatcher.getObj();

    this.events.addEventListener('CAMERA_EVENTS', this.onEvents);
    this.events.addEventListener('GAMESCENE_EVENTS', this.onEvents);
    this.events.addEventListener('PLAYER_EVENTS', this.onEvents);
    this.events.addEventListener('GAME_EVENTS', this.onEvents);

    this.position.copy(INITIAL_CAMERA_POSITION);
    this.initCameraFixed = false;
    this.shakeVal = DEFAULT_SHAKE_VALUE;
    this.lookAt(INITIAL_LOOK_AT_POSITION);

    this.panSpeed = {
      value: 0,
    };
  }
  updatePosForReplay() {
    this.position.copy(Global.playerPos);
    this.inActive = true;
  }
  /**
   * Handler for the event dispatched by the Player containing its kinematic data.
   * @param {object} data Event data containing position, rotationY, and isMoving.
   */
  onPlayerUpdate(data) {
    const { position, rotationY, isMoving } = data['message']['data'];

    // Store the latest kinematic data for the camera's update loop
    this._playerPos.set(position.x, position.y, position.z);
    this._playerRotY = rotationY;
    this._playerIsMoving = isMoving;
  }

  // Removed setFollowTarget as we are now using event data

  onEvents(data) {
    switch (data['message']['event_type']) {
      case 'reset_game':
        this.reset();
        break;
      case 'resize':
        this.onResize(data['message']['data']);
        break;
      case 'update':
        // Assuming 'update' event is tied to the main game loop
        this.update();
        break;
      case 'shake_camera':
        this.prepareToShake(data['message']['data']);
        break;
      case 'start_game':
        this.idleGap = { value: 0 };
        this.gameStarted = true;
        this.initCameraFixed = false;
        break;
      case 'kinematic_data':
        this.onPlayerUpdate(data);
        break;
      case 'update_replay_pos':
        this.updatePosForReplay();
        break;
      case 'start_intro':
        this.showIntro();
        break;
      case 'player_is_idle':
        this.onPlayerIdle();
        break;
      case 'player_is_move':
        this.onPlayerMove();
        break;
      case 'init_scene':
        this._lookTarget = null;
        this.lookAtData = null;
        break;
      case 'dispose':
        this.dispose();
        break;
    }
  }
  onPlayerMove() {
    this.isIdle = false;
    this.idleTwn && this.idleTwn.kill();
    this.moveTwn && this.moveTwn.kill();
    this.gapTwn && this.gapTwn.kill();
    this.moveTwn = gsap.to(this.isIdleInfo, {
      value: 0,
      duration: 2,
    });

    this.gapTwn = gsap.to(this.idleGap, {
      value: 0,
      duration: 2,
    });
  }
  onPlayerIdle() {
    this.isIdle = true;
    // this.isIdleInfo.value = 0;
    this.idleTwn && this.idleTwn.kill();
    this.moveTwn && this.moveTwn.kill();
    this.gapTwn && this.gapTwn.kill();

    this.idleTwn = gsap
      .timeline({
        repeat: -1, // Repeat indefinitely
        yoyo: false, // No need for yoyo since we define all steps
        defaults: {
          // Apply these defaults to all tweens in the timeline
          duration: 10,
          ease: 'power1.inOut',
        },
      })
      // Step 1: 0 to 1
      .to(this.isIdleInfo, { value: 0.6 })
      // Step 2: 1 to 0
      .to(this.isIdleInfo, { value: 0 })
      // Step 3: 0 to -1
      .to(this.isIdleInfo, { value: -0.6 })
      // Step 4: -1 to 0 (returns to start)
      .to(this.isIdleInfo, { value: 0 });

    this.gapTwn = gsap.to(this.idleGap, {
      value: 4,
      duration: 3,
      delay: 0.5,
      ease: 'power1.inOut',
    });
  }
  showIntro() {
    const fadeInObj = { value: 0 };

    // gsap.to(fadeInObj, {
    //   value: 1.0,
    //   duration: 1,
    //   delay: 0,
    //   ease: 'cubic.in',
    //   onStart: function () {}.bind(this),
    //   onUpdate: function () {
    //     this.renderer.toneMappingExposure = fadeInObj.value;
    //   }.bind(this),
    // });
    Global.introElapsed = { value: 1 };
    gsap.to(this.introbj, {
      value: 1,
      duration: 5 * Global.introPanTime,
      delay: 0,
      ease: 'power2.inOut',
    });
    gsap.to(this.introbj2, {
      value: 1,
      duration: 4 * Global.introPanTime,
      delay: 2 * Global.introPanTime,
      ease: 'power2.inOut',
      onComplete: function () {
        this.events.dispatchEvent({
          type: 'GAMESCENE_EVENTS',
          message: {
            event_type: 'start_game',
            data: {},
          },
        });
      }.bind(this),
    });
    // gsap.to(Global.introElapsed, {
    //   value: 0,
    //   duration: 4 * Global.introPanTime,
    //   delay: 3 * Global.introPanTime,
    //   ease: 'power2.inOut',
    // });
  }
  prepareToShake(data) {
    // if (Global.gameOver) return false;

    this.cameraclock = new THREE.Clock();
    this.canShakeCamera = true;
    this.shakeIntensity = {
      value: data['shake_fact'],
    };
    gsap.to(this.shakeIntensity, {
      duration: 0.5,
      value: 0,
      onComplete: function () {
        this.canShakeCamera = false;
      }.bind(this),
    });
  }

  reset() {
    this.initCameraFixed = false;
    this._currentMoveSpeed = 0;
    // Reset player state to initial values
    this._playerPos.set(0, 0, 0);
    this._playerRotY = 0;
    this._playerIsMoving = false;
  }

  onResize(data) {
    this.aspect = data.aspect;
    this.updateProjectionMatrix();
  }

  /**
   * Main camera update loop. Performs dynamic follow and look-at smoothing.
   */
  update() {
    if (!this.inActive) return;
    // Only proceed if we have received at least one player update
    if (
      this._playerPos.lengthSq() === 0 &&
      !this._playerIsMoving &&
      !this.gameStarted
    ) {
      return;
    }
    const targetPosition = INITIAL_LOOK_AT_POSITION.clone().lerp(
      this._playerPos /* .addScaledVector(
        new THREE.Vector3(0, 0, -10),
        1 * (1 - this.introbj.value)
      ) */,
      this.introbj.value
    ); //INITIAL_LOOK_AT_POSITION; //this._playerPos;

    const isMoving = this._playerIsMoving;

    // --- 1. Dynamic Camera Distance Calculation ---

    // LERP the actual speed factor (currentMoveSpeed) based on movement state
    if (isMoving) {
      this._currentMoveSpeed = THREE.MathUtils.lerp(
        this._currentMoveSpeed,
        1.0,
        MOVE_LERP_FACTOR //* Global.delta
      );
    } else {
      this._currentMoveSpeed = THREE.MathUtils.lerp(
        this._currentMoveSpeed,
        0.0,
        MOVE_LERP_FACTOR //* Global.delta
      );
    }
    const speedFactor = this._currentMoveSpeed;

    // Interpolate the camera distance between idle and max distance
    const currentCameraDistance = THREE.MathUtils.lerp(
      CAMERA_IDLE_DISTANCE + this.idleGap.value,
      CAMERA_MAX_DISTANCE,
      speedFactor //* Global.delta
    );
    // --- 2. Calculate Target Position ---

    const targetCameraPosition = new THREE.Vector3();
    targetCameraPosition.copy(targetPosition);

    // Get the player's forward direction vector
    const playerDirection = new THREE.Vector3(0, 0, 1);
    // Create a temporary Euler object to apply the Y rotation angle
    const playerRotation = new THREE.Euler(0, this._playerRotY, 0, 'XYZ');

    playerDirection.applyEuler(playerRotation); // Apply player's current rotation
    // Offset the position behind the player
    playerDirection.multiplyScalar(-currentCameraDistance);

    // Calculate the final target position
    targetCameraPosition.add(playerDirection);
    // Add height offset
    targetCameraPosition.y += CAMERA_HEIGHT + this.idleGap.value / 4;

    // --- 3. Smoothly Update Camera Position ---
    this.position.lerp(
      targetCameraPosition,
      POSITION_LERP_FACTOR * Global.delta * this.introbj.value * 10
    );
    this.camerastart = this.position.clone();
    // --- 4. Smoothly Update Camera LookAt Target ---
    // The point the camera should be looking at (player position + height offset)
    const actualLookTarget = new THREE.Vector3(
      targetPosition.x,
      targetPosition.y + CAMERA_LOOKAT_Y_PADD + this.idleGap.value / 4,
      targetPosition.z
    );
    // const extended = new THREE.Vector3(0, 0, -0.1)
    //   .applyEuler(this.rotation)
    //   .multiplyScalar(20);
    // actualLookTarget.add(extended);
    // actualLookTarget.addScalar()
    Global.cameraPos = this.position.clone();

    // Initialize or LERP the look target for a smooth effect
    if (!this._lookTarget) {
      this._lookTarget = actualLookTarget.clone();
    } else {
      this._lookTarget.lerp(
        actualLookTarget,
        LOOK_AT_LERP_FACTOR * Global.delta
      );
    }
    this.lookAt(
      INITIAL_LOOK_AT_POSITION.clone().lerp(
        this._lookTarget
          .clone()
          .addScaledVector(
            new THREE.Vector3(0.5, 0, 0).applyQuaternion(Global.playerQuot),
            this.isIdleInfo.value
          ),
        this.introbj2.value
      )
    );
    if (this.canShakeCamera) {
      const elapsed = this.cameraclock.getElapsedTime();
      if (elapsed < 0.5) {
        const shake =
          Math.sin(elapsed * 100) *
          this.shakeIntensity.value *
          (1 - elapsed / 1);
        this.position.x = this.camerastart.x + shake;
        this.position.y = this.camerastart.y + shake * 0.5;
        this.position.z =
          this.camerastart.z +
          shake * 0.5 +
          (isMobile ? this.zoomFact.value : this.zoomFact.value / 3);
      } else {
        this.position.copy(this.camerastart);
        this.position.z += isMobile
          ? this.zoomFact.value
          : this.zoomFact.value / 3;
      }
    }
  }

  dispose() {
    this.inActive = false;
    this.idleTwn && this.idleTwn.kill();
    this.moveTwn && this.moveTwn.kill();
    this.gapTwn && this.gapTwn.kill();
  }
}
